'use client';

// Progressive ("gradient") blur for sticky / fixed footers — the mirror image of
// the popsa.com header trick. Six layers are stacked in a zone anchored to the
// bottom edge; each has a fixed backdrop blur that doubles (1 -> 32px) and a mask
// that only reveals a thin horizontal band. The bands slide DOWNWARD as the blur
// strengthens, so page content scrolling under the footer goes from perfectly
// sharp at the top edge to heavily blurred at the bottom — instead of being
// hidden behind an opaque bar.

const LAYERS = [0, 1, 2, 3, 4, 5].map((i) => {
  const start = (i * 100) / 6; // band offset, 0%..83.3%
  return {
    blur: 2 ** i, // 1, 2, 4, 8, 16, 32 px
    mask:
      `linear-gradient(to bottom,` +
      ` rgba(0,0,0,0) ${start}%,` +
      ` rgb(0,0,0) ${start + 100 / 6}%,` +
      ` rgb(0,0,0) ${start + 200 / 6}%,` +
      ` rgba(0,0,0,0) ${start + 300 / 6}%)`,
  };
});

interface FooterProgressiveBlurProps {
  /** Height of the blur ramp in px. Taller = more gradual. Default 100. */
  height?: number;
  /**
   * Faint white gradient under the blur so a button keeps its contrast over
   * busy content. Set to false for a pure-blur footer. Default true.
   */
  wash?: boolean;
}

export default function FooterProgressiveBlur({
  height = 100,
  wash = true,
}: FooterProgressiveBlurProps) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height,
        pointerEvents: 'none',
      }}
    >
      {LAYERS.map(({ blur, mask }) => (
        <div
          key={blur}
          style={{
            position: 'absolute',
            inset: 0,
            backdropFilter: `blur(${blur}px)`,
            WebkitBackdropFilter: `blur(${blur}px)`,
            maskImage: mask,
            WebkitMaskImage: mask,
          }}
        />
      ))}
      {wash && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 70%, rgba(255,255,255,0.75) 100%)',
          }}
        />
      )}
    </div>
  );
}
